import React, { useRef, useEffect, useState, useMemo } from "react";
import PropTypes from "prop-types";
import {
    ComposedChart,
    Bar,
    XAxis,
    YAxis,
    Line,
    CartesianGrid,
    Legend,
    ResponsiveContainer,
    Tooltip,
    LabelList
} from "recharts";
import styles from "../../../../../components/barAndLineComboChart/barAndLineComboChart.module.css";

const BAR_WIDTH = 95; // only widening category width
const WINDOW_SIZE = 10;

/* ---------- FORMATTERS ---------- */
const formatNumber = (value) =>
    value !== undefined && value !== null
        ? value.toLocaleString()
        : "0";

/* ---------- SAFE VALUE FOR LOG SCALE ---------- */
const EPSILON = 0.0001;
const safeValue = (v) => (v > 0 ? v : EPSILON);

const formatToCappedUnit = (value) => {
    if (value === null || value === undefined) return null;
    if (value === EPSILON || value <= 0) return "0";

    if (value < 100000) {
        return `${(value / 1000).toFixed(1)} K`;
    }

    if (value < 10000000) {
        return `${(value / 100000).toFixed(1)} L`;
    }

    return `${(value / 10000000).toFixed(1)} Cr`;
};

const CustomizedAxisTick = ({ x, y, payload }) => (
    <g transform={`translate(${x},${y})`}>
        <text textAnchor="middle" fontSize={9} fill="#2B2B2B">
            {payload?.value}
        </text>
    </g>
);

const renderBlueBarLabel = ({ x, y, width, value }) => {
    if (value === null || value === undefined) return null;

    const label = formatToCappedUnit(value);
    if (!label) return null;

    return (
        <text
            x={x + width / 2}
            y={y - 14}
            textAnchor="middle"
            fill="#000000"
            fontSize={10}
            fontWeight={700}
        >
            {label}
        </text>
    );
};

const renderGreenBarLabel = ({ x, y, width, value }) => {
    if (value === null || value === undefined) return null;

    const label = formatToCappedUnit(value);
    if (!label) return null;

    return (
        <text
            x={x + width / 2}
            y={y - 2}
            textAnchor="middle"
            fill="#000000"
            fontSize={10}
            fontWeight={700}
        >
            {label}
        </text>
    );
};

const CompactTooltip = ({ active, payload, label, coordinate }) => {
    const payloadItems = Array.isArray(payload) ? payload : [];

    if (!active || payloadItems[0] === undefined || !coordinate) {
        return null;
    }

    const tooltipWidth = 180;
    const tooltipHeight = 70;

    const container = document.querySelector(`.${styles.scrollbarContainer}`);
    const rect = container?.getBoundingClientRect();
    if (!rect) return null;

    let left = rect.left + coordinate.x + 10;
    let top = rect.top + coordinate.y - tooltipHeight + 15;

    if (left + tooltipWidth > rect.right - 10) {
        left = rect.right - tooltipWidth - 10;
    }

    if (left < rect.left + 10) {
        left = rect.left + 10;
    }

    if (top < rect.top + 10) {
        top = rect.top + coordinate.y + 15;
    }

    if (top + tooltipHeight > rect.bottom) {
        top = rect.bottom - tooltipHeight - 10;
    }

    return (
        <div
            style={{
                position: "fixed",
                left,
                top,
                background: "#fff",
                border: "1px solid #d9d9d9",
                borderRadius: 4,
                padding: "6px 8px",
                fontSize: 10,
                boxShadow: "0 2px 6px rgba(0,0,0,0.15)",
                zIndex: 9999,
                pointerEvents: "none",
                width: tooltipWidth,
            }}
        >
            <div style={{ fontWeight: 600, marginBottom: 4 }}>{label}</div>

            {payloadItems.map((entry) => (
                <div key={entry.dataKey} style={{ color: entry.color }}>
                    {entry.name}:{" "}
                    {entry.value === EPSILON ? "0" : entry.value?.toLocaleString?.()}
                </div>
            ))}
        </div>
    );
};

const UnloadingPriorityGlobalGraph = ({ data = [], orderLossType, topNKey }) => {
    const [startIndex, setStartIndex] = useState(0);
    const chartContainerRef = useRef(null);

    /* ---------- WINDOWED DATA ---------- */
    const visibleData = useMemo(
        () => data.slice(startIndex, startIndex + WINDOW_SIZE),
        [data, startIndex]
    );

    /* ---------- HORIZONTAL SCROLL ---------- */
    useEffect(() => {
        const container = chartContainerRef.current;
        if (!container) return;

        const handleScroll = () => {
            const newStart = Math.floor(container.scrollLeft / BAR_WIDTH);
            if (
                newStart !== startIndex &&
                newStart >= 0 &&
                newStart <= Math.max(0, data.length - WINDOW_SIZE)
            ) {
                setStartIndex(newStart);
            }
        };

        container.addEventListener("scroll", handleScroll);
        return () => container.removeEventListener("scroll", handleScroll);
    }, [data, startIndex]);

    /* ---------- TRANSFORM DATA ---------- */
    const transformedData = useMemo(
        () =>
            visibleData.map((item) => ({
                orderLossFifo: safeValue(
                    item[`${orderLossType} by Value on FIFO (Top ${topNKey})`] || 0
                ),
                orderLossWeightedAverage: safeValue(
                    item[
                    `${orderLossType} by Value on weighted average (Top ${topNKey})`
                    ] || 0
                ),
                receivingPlant: item["Receiving Plant"] || "",
                totalShipment: item["Total Shipment"] || 0,
            })),
        [visibleData, orderLossType, topNKey]
    );

    /* ---------- RIGHT AXIS DOMAIN ---------- */
    const shipmentMax = Math.max(
        ...transformedData.map((d) => d.totalShipment),
        0
    );

    const rightDomain = [0, Math.ceil(shipmentMax * 1.2)];
    const isScrollable = data.length > WINDOW_SIZE;

    return (
        <div
            className={styles.scrollbarContainer}
            ref={chartContainerRef}
            style={{
                overflowX: isScrollable ? "auto" : "hidden",
                width: "100%",
                position: "relative",
                marginTop: "5px",
            }}
        >
            {/* phantom width for scroll */}
            <div
                style={{
                    width: isScrollable ? `${data.length * BAR_WIDTH}px` : "100%",
                    height: 280, // keep original height
                    position: "relative",
                    overflow: "visible",
                }}
            >
                {/* windowed chart */}
                <div
                    style={{
                        position: "absolute",
                        left: `${startIndex * BAR_WIDTH}px`,
                        width: isScrollable ? `${WINDOW_SIZE * BAR_WIDTH}px` : "100%",
                        height: 280, // keep original height
                    }}
                >
                    <ResponsiveContainer width="100%" height="100%">
                        <ComposedChart
                            data={transformedData}
                            barGap={8}
                            barCategoryGap={22}
                            margin={{ top: 40, right: 40, bottom: 40, left: 40 }}
                        >
                            <CartesianGrid strokeDasharray="3 3" vertical={false} />

                            {/* X AXIS */}
                            <XAxis
                                dataKey="receivingPlant"
                                tick={<CustomizedAxisTick />}
                                interval={0}
                                height={50}
                                axisLine={false}
                                tickLine={false}
                                tickMargin={10}
                            />

                            {/* LEFT Y AXIS – ORDER LOSS (LOG SCALE) */}
                            <YAxis
                                yAxisId="left"
                                scale="log"
                                domain={["dataMin", "dataMax"]}
                                tickFormatter={formatNumber}
                                tick={{ fontSize: 10 }}
                                axisLine={false}
                                tickLine={false}
                                width={90}
                            />

                            {/* RIGHT Y AXIS – SHIPMENTS (LINEAR) */}
                            <YAxis
                                yAxisId="right"
                                orientation="right"
                                domain={rightDomain}
                                tickFormatter={(val) => Math.round(val)}
                                allowDecimals={false}
                                tick={{ fontSize: 10 }}
                                axisLine={false}
                                tickLine={false}
                                width={55}
                            />

                            {/* TOOLTIP */}
                            <Tooltip
                                content={<CompactTooltip />}
                                isAnimationActive={false}
                                allowEscapeViewBox={{ x: true, y: true }}
                                cursor={{ fill: "rgba(0,0,0,0.05)" }}
                            />

                            <Legend
                                verticalAlign="bottom"
                                align="left"
                                wrapperStyle={{ fontSize: "10px", color: "black" }}
                            />

                            {/* BLUE BAR */}
                            <Bar
                                yAxisId="left"
                                dataKey="orderLossWeightedAverage"
                                name={`${orderLossType} by Value on Weighted Average (Top ${topNKey})`}
                                fill="rgb(0, 94, 239)"
                                barSize={28}
                                minPointSize={2}
                            >
                                <LabelList content={renderBlueBarLabel} />
                            </Bar>

                            {/* GREEN BAR */}
                            <Bar
                                yAxisId="left"
                                dataKey="orderLossFifo"
                                name={`${orderLossType} by Value on FIFO (Top ${topNKey})`}
                                fill="rgb(0, 177, 144)"
                                barSize={28}
                                minPointSize={2}
                            >
                                <LabelList content={renderGreenBarLabel} />
                            </Bar>

                            {/* LINE */}
                            <Line
                                yAxisId="right"
                                type="bump"
                                dataKey="totalShipment"
                                name="Total Shipment"
                                stroke="#e7381d"
                                strokeWidth={1.3}
                                strokeOpacity={0.9}
                                dot={{ r: 3 }}
                                activeDot={{ r: 5 }}
                            />
                        </ComposedChart>
                    </ResponsiveContainer>
                </div>
            </div>
        </div>
    );
};

UnloadingPriorityGlobalGraph.propTypes = {
    data: PropTypes.arrayOf(PropTypes.object),
    orderLossType: PropTypes.string,
    topNKey: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
};

CustomizedAxisTick.propTypes = {
    x: PropTypes.number,
    y: PropTypes.number,
    payload: PropTypes.shape({
        value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    }),
};

CompactTooltip.propTypes = {
    active: PropTypes.bool,
    payload: PropTypes.arrayOf(
        PropTypes.shape({
            dataKey: PropTypes.string,
            color: PropTypes.string,
            name: PropTypes.string,
            value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        })
    ),
    label: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    coordinate: PropTypes.shape({
        x: PropTypes.number,
        y: PropTypes.number,
    }),
};

export default UnloadingPriorityGlobalGraph;